import { useQuery, useMutation } from '@tanstack/react-query'
import client from '@/ports/api-client'
import {
  purchaseStandaloneWithDiscount,
  boostSponsoredListing,
  getProductData
} from '@/models/plans/apis'
import { CatalogResponse } from '@/features/Payments/types'

const getSponsoredListing = async (listingId: string) => {
  try {
    return await client.get(`/listings/${listingId}/sponsored`, { noLoading: true })
  } catch (error) {
    throw error
  }
}

type NewBoost = {
  listingId: number | string
  onSuccess: (a?: any) => void
  body: {
    planned_start_date?: string
    planned_end_date?: string
    planned_start_time?: string
    plan_name: any
    product_name: string
    listing_id: number | string
    immediate_start?: boolean
  }
}

type SponsoredListingPayload = {
  listingId: number | string
  body: {
    planned_start_date?: string
    planned_end_date?: string
    planned_start_time?: string
    plan_name: any
    product_name: string
    listing_id: number | string
    immediate_start?: boolean
  }
}

type PurchaseData = {
  planName: string
  paymentMethodId: string
  discountCode: string
  onSuccess?: () => void
}

const handleSponsoredListingPurchase = async (purchaseData: PurchaseData, listingId: string) => {
  return purchaseStandaloneWithDiscount(
    {
      plan_name: purchaseData.planName,
      payment_method_id: purchaseData.paymentMethodId,
      listing_id: listingId
    },
    { couponCode: purchaseData.discountCode }
  )
}

const useSponsorListing = (listingId: string) => {
  const purchaseSponsoredListing = useMutation({
    mutationFn: (purchaseInfo: PurchaseData) =>
      handleSponsoredListingPurchase(purchaseInfo, listingId),
    onSuccess: async (result, variables) => {
      const response = result?.response
      if (response?.ok) {
        variables.onSuccess?.()
      }
    }
  })

  const boostListing = useMutation({
    mutationFn: (newBoost: NewBoost) => {
      return boostSponsoredListing(newBoost, { listingId: newBoost.listingId })
    },
    onSuccess: async (data, variables) => {
      variables.onSuccess(data)
    }
  })

  return { purchaseSponsoredListing, boostListing }
}

const useBoostListingWithExistingCredits = () => {
  const boostExistingListing = useMutation({
    mutationFn: (newBoost: NewBoost) => {
      return boostSponsoredListing(newBoost, { listingId: newBoost.listingId })
    },
    onSuccess: async (data, variables) => {
      variables.onSuccess(data)
    }
  })

  return boostExistingListing
}

const useGetProduct = (productName: string) => {
  const { data, isLoading } = useQuery<CatalogResponse>({
    queryKey: [`products`, productName],
    queryFn: () => getProductData(productName),
    refetchOnWindowFocus: false,
    staleTime: 60000 * 5,
    enabled: !!productName
  })
  return { data, isLoading }
}

const useGetSponsoredListing = (listingId: string) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [`sponsored-listings`, listingId],
    queryFn: () => getSponsoredListing(listingId),
    refetchOnWindowFocus: false,
    staleTime: 60000 * 5,
    enabled: !!listingId
  })
  return { data, isLoading, refetch }
}

async function getData(listingId: string) {
  return client.get(`/web/listings/${listingId}/fetch`)
}

async function postNewSponsoredListing(newBoost: SponsoredListingPayload) {
  return boostSponsoredListing(newBoost, { listingId: newBoost.listingId })
}

const useGetSuiteInfo = (listingId: string) => {
  const { data, isLoading } = useQuery({
    queryKey: [`listing-info-${listingId}`],
    queryFn: () => getData(listingId),
    refetchOnWindowFocus: false
  })
  return { data, isLoading }
}

export {
  postNewSponsoredListing,
  useGetProduct,
  useSponsorListing,
  useGetSponsoredListing,
  useGetSuiteInfo,
  useBoostListingWithExistingCredits
}
